import { FrameType, type TourFrame, type TourStep } from '@carboncopyco/types';
import { AnalyticsTrackEventName, useAnalytics } from '@carboncopyco/utils';
import { type RefObject, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useTourDispatch } from '../../../../../../hooks';
import { useNotify } from '../../../../../../hooks/useNotify';
import { useTourEditorPageProps } from '../../../../../../pages/TourEditor/TourEditorPageContext';
import {
  selectActiveStepId,
  selectAddingStepInProgress,
  setAddingStepInProgress,
} from '../../../../../../store';
import type { ReplayerEditorRef } from '../../../../ToursReplayerEditor';

interface Props {
  uuid: string;
  frameId: number;
  tourId: number;
  replayerRef: RefObject<ReplayerEditorRef>;
}

const useAddStepActions = ({ uuid, frameId, tourId, replayerRef }: Props) => {
  const { trackEvent } = useAnalytics();
  const dispatch = useTourDispatch();
  const isAddingStepInProgress = useSelector(selectAddingStepInProgress(uuid));
  const { rootElement } = useTourEditorPageProps();
  const { showNotify, closeNotify } = useNotify(rootElement);
  const activeStepId = useSelector(selectActiveStepId(uuid));

  const cancelAddingStep = (
    stepIdToPlay?: TourStep['stepId'],
    omitPlayingStep = false
  ) => {
    dispatch(setAddingStepInProgress({ uuid, inProgress: false }));
    replayerRef.current.cancelInspectMode();
    closeNotify();

    trackEvent(AnalyticsTrackEventName.TOUR_CLICK_CANCEL_INSPECT_MODE, {
      location: 'tour_editor_snackbar',
      tourId: tourId,
      frameId: frameId,
    });

    if (omitPlayingStep) {
      return;
    }

    replayerRef.current.playStep(stepIdToPlay || activeStepId);
  };

  const handleAddingStep = (frame: TourFrame) => {
    if (isAddingStepInProgress) {
      return;
    }

    replayerRef.current?.addStep(frame);
    dispatch(setAddingStepInProgress({ uuid, inProgress: true }));
    const isRecordingFrame = frame.frameType === FrameType.recording;

    showNotify({
      message: isRecordingFrame
        ? 'Inspect the element you want the tooltip to be attached to'
        : 'Create area you want the tooltip to be attached to',
      actionText: 'Cancel',
      onActionClick: () => cancelAddingStep(),
    });

    trackEvent(AnalyticsTrackEventName.TOUR_CLICK_ADD_STEP_TO_FRAME, {
      location: 'tour_editor_frame',
      tourId: frame.tourData.id,
      frameId: frame.id,
    });
  };

  /**
   * Set keydown event listener
   */
  useEffect(() => {
    if (isAddingStepInProgress) {
      const onKeyDown = ({ key }: { key: string }) => {
        if (key === 'Escape') {
          cancelAddingStep();
        }
      };
      window.addEventListener('keydown', onKeyDown);
      return () => window.removeEventListener('keydown', onKeyDown);
    }
  }, [isAddingStepInProgress]);

  return {
    handleAddingStep,
    cancelAddingStep,
  };
};

export default useAddStepActions;
